import { ROW_ERROR_FIELD } from '@/lib/map-field-errors';

import { createEmptyRows } from './helpers';
import type { CellErrors, SampleManagerAction, SampleManagerState } from './types';

// Re-export from helpers for backward compatibility
export { createEmptyRows };

/**
 * Reducer for managing SampleManager state
 */
export const sampleManagerReducer = (state: SampleManagerState, action: SampleManagerAction): SampleManagerState => {
  switch (action.type) {
    case 'UPLOAD_START':
      return {
        ...state,
        isProcessing: true,
        errorMessage: null,
      };

    case 'UPLOAD_SUCCESS':
      return {
        ...state,
        hasFileBeenUploaded: true,
        rowData: action.payload.rowData,
        errorMessage: null,
        isProcessing: false,
        cellErrors: {},
      };

    // Syncs rowData from form state (e.g. draft load) without setting hasFileBeenUploaded,
    // preserving the ability to change the sample count after the draft is populated.
    case 'SYNC_FROM_FORM':
      return {
        ...state,
        rowData: action.payload.rowData,
      };

    case 'UPLOAD_ERROR':
      return {
        ...state,
        hasFileBeenUploaded: false,
        errorMessage: action.payload.errorMessage,
        isProcessing: false,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        errorMessage: null,
      };

    case 'UPDATE_ROW_COUNT':
      return {
        ...state,
        rowData: createEmptyRows(action.payload.count),
      };

    case 'UPDATE_ROW': {
      const { rowIndex, patch } = action.payload;
      const updatedRows = state.rowData.map((row, i) => (i === rowIndex ? { ...row, ...patch } : row));

      let nextCellErrors = state.cellErrors;
      const existing = state.cellErrors[rowIndex];
      if (existing) {
        const pruned = { ...existing };
        for (const field of Object.keys(patch)) {
          delete pruned[field];
        }
        // A whole-row backend error (ROW_ERROR_FIELD) carries no concrete column, so it would
        // never be pruned by the patched-field loop above and would stay stuck until the next
        // submit. Clear it on any edit to the row — the user has changed the row's data.
        delete pruned[ROW_ERROR_FIELD];
        nextCellErrors = { ...state.cellErrors };
        if (Object.keys(pruned).length === 0) delete nextCellErrors[rowIndex];
        else nextCellErrors[rowIndex] = pruned;
      }

      return {
        ...state,
        rowData: updatedRows,
        cellErrors: nextCellErrors,
      };
    }

    case 'RESIZE_ROWS': {
      const { count, existingRows } = action.payload;
      // Generate all rows with correct well positions and demux indices for the new count
      const allGeneratedRows = createEmptyRows(count);
      // Overlay existing user data onto the generated rows, preserving entered values
      const mergedRows = allGeneratedRows.map((generatedRow, i) => {
        if (i < existingRows.length) {
          // Preserve user-provided fields (name, study, org, etc.) from existingRows,
          // but always use freshly generated well_position and demux_index so that
          // position metadata is recalculated correctly for the new total count.
          return {
            ...existingRows[i],
            well_position: generatedRow.well_position,
            demux_index: generatedRow.demux_index,
          };
        }
        // New slot — use the generated row with correct position metadata
        return generatedRow;
      });
      return {
        ...state,
        rowData: mergedRows,
        cellErrors: {},
      };
    }

    case 'SET_CELL_ERRORS': {
      const next: CellErrors = {};
      for (const { row, field, msg } of action.payload.triples) {
        if (!next[row]) next[row] = {};
        next[row][field] = msg;
      }
      return { ...state, cellErrors: next };
    }

    case 'CLEAR_CELL_ERRORS':
      return { ...state, cellErrors: {} };

    case 'CLEAR_CELL_ERROR': {
      const { rowIndex, field } = action.payload;
      if (!state.cellErrors[rowIndex]?.[field]) return state;
      const nextRow = { ...state.cellErrors[rowIndex] };
      delete nextRow[field];
      const nextCellErrors = { ...state.cellErrors };
      if (Object.keys(nextRow).length === 0) delete nextCellErrors[rowIndex];
      else nextCellErrors[rowIndex] = nextRow;
      return { ...state, cellErrors: nextCellErrors };
    }

    case 'RESET':
      return {
        hasFileBeenUploaded: false,
        rowData: createEmptyRows(4),
        errorMessage: null,
        isProcessing: false,
        cellErrors: {},
      };

    default:
      return state;
  }
};
